import React, { useState } from 'react';
import { motion } from 'framer-motion';
// Direct imports to avoid circular dependencies
import { useCelebrations } from '../components/micro-interactions/CelebrationProvider';
import { fireConfetti, fireStars } from '../components/micro-interactions/Celebrations';
import {
  AnimatedNumber,
  AnimatedCurrency,
  AnimatedPercentage,
  SlotMachineNumber,
  ValueFlash,
  TrendingIndicator,
} from '../components/micro-interactions/AnimatedValue';
import {
  StaggeredList,
  CascadingGrid,
  FadeStagger,
} from '../components/micro-interactions/AnimatedList';
import {
  TiltCard,
  HoverLiftCard,
  GlowCard,
  PulseCard,
} from '../components/micro-interactions/AnimatedCard';
import {
  AnimatedButton,
  RippleButton,
  MagneticButton,
  IconButton,
} from '../components/micro-interactions/AnimatedButton';
import {
  ScrollReveal,
  ScrollProgressBar,
} from '../components/micro-interactions/ScrollAnimations';
import {
  PageTransition,
  StaggeredPageContent,
} from '../components/micro-interactions/PageTransitions';
import { Icons } from '../components/Icons';

// Demo card component
const DemoCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    className="glass-card"
    style={{
      padding: 24,
      borderRadius: 16,
      marginBottom: 24,
    }}
  >
    <h3 style={{ marginBottom: 16, fontSize: '1.25rem', fontWeight: 600 }}>{title}</h3>
    {children}
  </div>
);

// Demo section
const DemoSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 48 }}>
    <h2 style={{
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: 24,
      paddingBottom: 8,
      borderBottom: '2px solid var(--accent)',
    }}>
      {title}
    </h2>
    {children}
  </section>
);

export const MicroInteractionsDemo: React.FC = () => {
  const celebrations = useCelebrations();

  // Demo state
  const [value, setValue] = useState(12345.67);
  const [percentage, setPercentage] = useState(15.5);
  const [slotNumber, setSlotNumber] = useState(42);
  const [flashTrigger, setFlashTrigger] = useState(0);
  const [heartActive, setHeartActive] = useState(false);

  return (
    <PageTransition type="slideUp">
      <ScrollProgressBar color="var(--accent)" />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <StaggeredPageContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <h1 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: 16 }}>
              ✨ Micro-Interactions Demo
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: '1.1rem' }}>
              Bibliothèque d'animations inspirées de Rive.app pour WealthHub Crypto
            </p>
          </motion.div>
        </StaggeredPageContent>

        {/* Celebrations Section */}
        <ScrollReveal>
          <DemoSection title="🎉 Célébrations">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <RippleButton
                onClick={() => celebrations.triggerConfetti()}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: '#1a1a1a',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                🎊 Confetti
              </RippleButton>

              <RippleButton
                onClick={() => celebrations.triggerRocket('+25% 🚀')}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                🚀 Rocket (+10%)
              </RippleButton>

              <RippleButton
                onClick={() => celebrations.triggerATH('BTC', '$150,000')}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FF6347 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: '#1a1a1a',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                👑 ATH Reached
              </RippleButton>

              <RippleButton
                onClick={() => celebrations.triggerTargetReached('Take Profit #1', '$100,000')}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                🎯 Target Reached
              </RippleButton>

              <RippleButton
                onClick={() => celebrations.triggerFireworks(3000)}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                🎆 Fireworks
              </RippleButton>

              <RippleButton
                onClick={() => celebrations.triggerCoins()}
                style={{
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: '#1a1a1a',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                💰 Coins Rain
              </RippleButton>
            </div>
          </DemoSection>
        </ScrollReveal>

        {/* Animated Values Section */}
        <ScrollReveal direction="left">
          <DemoSection title="📊 Valeurs Animées">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
              <DemoCard title="Animated Number">
                <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 16 }}>
                  <AnimatedNumber
                    value={value}
                    prefix="$"
                    showChange
                    highlightChange
                  />
                </div>
                <button
                  onClick={() => setValue(prev => prev + Math.random() * 1000 - 500)}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--accent)',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Randomize
                </button>
              </DemoCard>

              <DemoCard title="Animated Currency">
                <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 16 }}>
                  <AnimatedCurrency value={value} currency="EUR" showChange />
                </div>
                <div style={{ fontSize: '1.5rem', opacity: 0.7 }}>
                  <AnimatedCurrency value={value * 1000000} currency="USD" compact />
                </div>
              </DemoCard>

              <DemoCard title="Animated Percentage">
                <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 16 }}>
                  <AnimatedPercentage value={percentage} colorCode showSign />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setPercentage(prev => prev + 5)}
                    style={{
                      padding: '8px 16px',
                      background: '#22c55e',
                      border: 'none',
                      borderRadius: 8,
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    +5%
                  </button>
                  <button
                    onClick={() => setPercentage(prev => prev - 5)}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: 8,
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    -5%
                  </button>
                </div>
              </DemoCard>

              <DemoCard title="Slot Machine Number">
                <div style={{ fontSize: '3rem', fontWeight: 700, fontFamily: 'monospace', marginBottom: 16 }}>
                  <SlotMachineNumber value={slotNumber} />
                </div>
                <button
                  onClick={() => setSlotNumber(Math.floor(Math.random() * 1000))}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--accent)',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Spin!
                </button>
              </DemoCard>

              <DemoCard title="Value Flash">
                <ValueFlash trigger={flashTrigger} flashColor="rgba(59, 130, 246, 0.4)">
                  <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                    ${(flashTrigger * 100).toFixed(2)}
                  </div>
                </ValueFlash>
                <button
                  onClick={() => setFlashTrigger(prev => prev + 1)}
                  style={{
                    marginTop: 16,
                    padding: '8px 16px',
                    background: 'var(--accent)',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Update Value
                </button>
              </DemoCard>

              <DemoCard title="Trending Indicator">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <TrendingIndicator value={150} previousValue={100} size="lg" />
                  <TrendingIndicator value={80} previousValue={100} size="md" />
                  <TrendingIndicator value={100} previousValue={100} size="sm" />
                </div>
              </DemoCard>
            </div>
          </DemoSection>
        </ScrollReveal>

        {/* Animated Cards Section */}
        <ScrollReveal direction="right">
          <DemoSection title="🃏 Cards Animées">
            <CascadingGrid columns={3} gap={24}>
              <TiltCard
                tiltAmount={15}
                glareEnabled
                style={{ height: 200 }}
              >
                <div
                  className="glass-card"
                  style={{
                    height: '100%',
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 16,
                  }}
                >
                  <Icons.Sparkles size={48} />
                  <h2 style={{ marginTop: 16 }}>3D Tilt Card</h2>
                  <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                    Survole pour effet 3D
                  </p>
                </div>
              </TiltCard>

              <HoverLiftCard
                liftAmount={12}
                shadowIntensity="high"
                style={{ height: 200 }}
              >
                <div
                  className="glass-card"
                  style={{
                    height: '100%',
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 16,
                  }}
                >
                  <Icons.TrendingUp size={48} />
                  <h2 style={{ marginTop: 16 }}>Hover Lift Card</h2>
                  <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                    Survole pour élévation
                  </p>
                </div>
              </HoverLiftCard>

              <GlowCard
                glowColor="#3b82f6"
                glowIntensity={0.6}
                style={{ height: 200 }}
              >
                <div
                  className="glass-card"
                  style={{
                    height: '100%',
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 16,
                  }}
                >
                  <Icons.Zap size={48} />
                  <h2 style={{ marginTop: 16 }}>Glow Card</h2>
                  <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                    Effet lueur au curseur
                  </p>
                </div>
              </GlowCard>

              <PulseCard
                pulseColor="#22c55e"
                isActive
                style={{ height: 200 }}
              >
                <div
                  className="glass-card"
                  style={{
                    height: '100%',
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 16,
                  }}
                >
                  <Icons.Bell size={48} />
                  <h2 style={{ marginTop: 16 }}>Pulse Card</h2>
                  <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                    Animation d'attention
                  </p>
                </div>
              </PulseCard>
            </CascadingGrid>
          </DemoSection>
        </ScrollReveal>

        {/* Buttons Section */}
        <ScrollReveal>
          <DemoSection title="🔘 Boutons Animés">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
              <AnimatedButton
                onClick={async () => {
                  await new Promise(resolve => setTimeout(resolve, 1500));
                }}
                variant="primary"
                size="lg"
                loadingText="Chargement..."
                successText="Succès!"
              >
                Animated Button
              </AnimatedButton>

              <RippleButton
                onClick={() => fireConfetti()}
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                Ripple Button
              </RippleButton>

              <MagneticButton
                onClick={() => fireStars()}
                strength={0.4}
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Magnetic Button
              </MagneticButton>

              <IconButton
                icon={<Icons.Heart size={24} />}
                activeIcon={<Icons.Heart size={24} />}
                isActive={heartActive}
                onClick={() => setHeartActive(!heartActive)}
                size="lg"
                style={{
                  background: heartActive ? '#ef4444' : 'rgba(255,255,255,0.1)',
                }}
              />
            </div>
          </DemoSection>
        </ScrollReveal>

        {/* Staggered Lists Section */}
        <ScrollReveal direction="up">
          <DemoSection title="📋 Listes Staggered">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
              <DemoCard title="Staggered List">
                <StaggeredList staggerDelay={0.1} direction="up">
                  {['Bitcoin', 'Ethereum', 'Solana', 'Cardano', 'Polkadot'].map((name, i) => (
                    <div
                      key={name}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: 8,
                        marginBottom: 8,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{name}</span>
                      <AnimatedPercentage value={(i + 1) * 5.5} colorCode showSign />
                    </div>
                  ))}
                </StaggeredList>
              </DemoCard>

              <DemoCard title="Fade Stagger">
                <FadeStagger staggerDelay={0.15}>
                  {['🟢 Online', '🟡 Syncing', '🔴 Error', '⚪ Offline'].map((status) => (
                    <div
                      key={status}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: 8,
                        marginBottom: 8,
                      }}
                    >
                      {status}
                    </div>
                  ))}
                </FadeStagger>
              </DemoCard>
            </div>
          </DemoSection>
        </ScrollReveal>

        {/* Milestone Tests */}
        <ScrollReveal>
          <DemoSection title="🏆 Test Milestones">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { type: 'gain_10', label: '+10% Gain', color: '#3b82f6' },
                { type: 'gain_25', label: '+25% Gain', color: '#8b5cf6' },
                { type: 'gain_50', label: '+50% Gain', color: '#ec4899' },
                { type: 'gain_100', label: '+100% (2x)', color: '#f59e0b' },
                { type: 'first_profit', label: 'First Profit', color: '#22c55e' },
                { type: 'streak', label: 'Win Streak', color: '#06b6d4' },
                { type: 'transaction', label: 'Transaction', color: '#84cc16' },
              ].map(({ type, label, color }) => (
                <RippleButton
                  key={type}
                  onClick={() => celebrations.triggerMilestone(type as any, {
                    tokenSymbol: 'BTC',
                    percentGain: parseInt(type.split('_')[1]) || 10,
                    streakCount: 5,
                  })}
                  style={{
                    padding: '12px 16px',
                    background: color,
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {label}
                </RippleButton>
              ))}
            </div>
          </DemoSection>
        </ScrollReveal>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>
          <p>Built with Framer Motion + canvas-confetti</p>
          <p style={{ fontSize: '0.875rem', marginTop: 8 }}>
            Inspired by Rive.app animations
          </p>
        </div>
      </div>
    </PageTransition>
  );
};

export default MicroInteractionsDemo;
